import {
  GLCodingResponse,
  GLCodingSeedRequest,
} from './components/financial/gl-coding/types'

// Simulate a single API call that returns all dimension data
export async function fetchGLCodingData(
  seed: GLCodingSeedRequest,
): Promise<GLCodingResponse> {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 500))

  // Determine which dimensions to return based on vendor/dimension count
  const dimensionCount = seed.dimensionCount || 5 // Default to 5 if not specified

  let dimensionNames: string[] = []

  if (dimensionCount === 3) {
    // Simple: Department, Cost Centre, Region
    dimensionNames = ['Department', 'Cost Centre', 'Region']
  } else if (dimensionCount === 4) {
    // Add Project
    dimensionNames = ['Department', 'Cost Centre', 'Project', 'Region']
  } else if (dimensionCount === 5) {
    // Standard
    dimensionNames = [
      'Department',
      'Cost Centre',
      'Project',
      'Activity',
      'Region',
    ]
  } else if (dimensionCount >= 9) {
    // Complex: All dimensions
    dimensionNames = [
      'Business Unit',
      'Department',
      'Cost Centre',
      'Location',
      'Project',
      'Activity',
      'Product Line',
      'Customer',
      'Region',
    ]
  }

  // Mock response with realistic D365 F&O dimension structure
  return {
    dimensionNames,
    separator: '-',
    dimensionData: {
      'Business Unit': {
        values: [
          { code: 'BU01', description: 'Corporate' },
          { code: 'BU02', description: 'Manufacturing' },
          { code: 'BU03', description: 'Retail' },
          { code: 'BU04', description: 'Services' },
          { code: 'BU05', description: 'Technology' },
          { code: 'BU06', description: 'Healthcare' },
          { code: 'BU07', description: 'Financial Services' },
          { code: 'BU08', description: 'Education' },
          { code: 'BU09', description: 'Government' },
          { code: 'BU10', description: 'Non-Profit' },
          { code: 'BU11', description: 'Real Estate' },
          { code: 'BU12', description: 'Transportation' },
          { code: 'BU13', description: 'Energy' },
          { code: 'BU14', description: 'Telecommunications' },
          { code: 'BU15', description: 'Media & Entertainment' },
          { code: 'BU16', description: 'Hospitality' },
          { code: 'BU17', description: 'Agriculture' },
          { code: 'BU18', description: 'Construction' },
          { code: 'BU19', description: 'Pharmaceutical' },
          { code: 'BU20', description: 'Automotive' },
        ],
      },
      Department: {
        values: [
          { code: '100', description: 'Finance' },
          { code: '110', description: 'Treasury & Cash Management' },
          { code: '120', description: 'Financial Planning & Analysis' },
          { code: '130', description: 'Tax & Compliance' },
          { code: '140', description: 'Audit & Risk' },
          { code: '200', description: 'Operations' },
          { code: '210', description: 'Manufacturing' },
          { code: '220', description: 'Quality Assurance' },
          { code: '230', description: 'Supply Chain' },
          { code: '240', description: 'Procurement' },
          { code: '300', description: 'Sales' },
          { code: '310', description: 'Business Development' },
          { code: '320', description: 'Customer Success' },
          { code: '330', description: 'Sales Operations' },
          { code: '400', description: 'IT' },
          { code: '410', description: 'Information Security' },
          { code: '420', description: 'Data & Analytics' },
          { code: '500', description: 'Human Resources' },
          { code: '510', description: 'Talent Acquisition' },
          { code: '520', description: 'Learning & Development' },
          { code: '600', description: 'Marketing' },
          { code: '610', description: 'Product Marketing' },
          { code: '620', description: 'Digital Marketing' },
          { code: '700', description: 'Legal' },
          { code: '800', description: 'Executive' },
        ],
        validCombinations:
          dimensionCount >= 9
            ? {
                BU01: ['100', '110', '120', '130', '140', '500', '700', '800'],
                BU02: ['200', '210', '220', '230', '240'],
                BU03: ['300', '310', '320', '330'],
                BU04: ['300', '320', '500'],
                BU05: ['400', '410', '420', '600', '610', '620'],
                BU06: ['100', '200', '300', '400', '500'],
                BU07: ['100', '110', '120', '130', '400'],
                BU08: ['100', '400', '500', '600'],
                BU09: ['100', '200', '400', '500', '700'],
                BU10: ['100', '300', '500', '600'],
                BU11: ['100', '200', '300', '500'],
                BU12: ['200', '230', '240', '400'],
                BU13: ['200', '210', '400', '500'],
                BU14: ['300', '400', '410', '600'],
                BU15: ['300', '600', '610', '620'],
                BU16: ['200', '300', '500', '600'],
                BU17: ['200', '210', '230', '240'],
                BU18: ['200', '210', '240', '500'],
                BU19: ['200', '210', '220', '400'],
                BU20: ['200', '210', '230', '300'],
              }
            : undefined,
      },
      'Cost Centre': {
        values: [
          { code: '1010', description: 'Accounting' },
          { code: '1020', description: 'Treasury' },
          { code: '1030', description: 'FP&A' },
          { code: '1040', description: 'Payroll' },
          { code: '1050', description: 'Tax' },
          { code: '1060', description: 'Internal Audit' },
          { code: '1070', description: 'Risk Management' },
          { code: '1080', description: 'Compliance' },
          { code: '2010', description: 'Manufacturing' },
          { code: '2020', description: 'Logistics' },
          { code: '2030', description: 'Warehouse Operations' },
          { code: '2040', description: 'Quality Control' },
          { code: '2050', description: 'Production Planning' },
          { code: '2060', description: 'Procurement' },
          { code: '2070', description: 'Vendor Management' },
          { code: '3010', description: 'Direct Sales' },
          { code: '3020', description: 'Channel Sales' },
          { code: '3030', description: 'Inside Sales' },
          { code: '3040', description: 'Sales Engineering' },
          { code: '3050', description: 'Customer Support' },
          { code: '3060', description: 'Account Management' },
          { code: '4010', description: 'Infrastructure' },
          { code: '4020', description: 'Applications' },
          { code: '4030', description: 'Security Operations' },
          { code: '4040', description: 'Data Engineering' },
          { code: '4050', description: 'Business Intelligence' },
          { code: '4060', description: 'IT Support' },
          { code: '5010', description: 'Recruitment' },
          { code: '5020', description: 'HR Operations' },
          { code: '5030', description: 'Training' },
          { code: '5040', description: 'Compensation & Benefits' },
        ],
        validCombinations: {
          '100': ['1010', '1020', '1030', '1040', '1050'],
          '110': ['1020', '1030'],
          '120': ['1030'],
          '130': ['1050', '1080'],
          '140': ['1060', '1070'],
          '200': ['2010', '2020', '2030', '2040', '2050'],
          '210': ['2010', '2050'],
          '220': ['2040'],
          '230': ['2020', '2030', '2060'],
          '240': ['2060', '2070'],
          '300': ['3010', '3020', '3030', '3040', '3050', '3060'],
          '310': ['3010', '3020'],
          '320': ['3050', '3060'],
          '330': ['3030', '3040'],
          '400': ['4010', '4020', '4030', '4040', '4050', '4060'],
          '410': ['4030'],
          '420': ['4040', '4050'],
          '500': ['5010', '5020', '5030', '5040'],
          '510': ['5010'],
          '520': ['5030'],
          '600': ['3010', '3020', '3030'],
          '610': ['3010'],
          '620': ['3030'],
          '700': ['1080'],
          '800': ['1010', '1020'],
        },
      },
      Location: {
        values: [
          { code: 'LOC-HQ', description: 'Headquarters - New York' },
          { code: 'LOC-SF', description: 'San Francisco Office' },
          { code: 'LOC-LA', description: 'Los Angeles Office' },
          { code: 'LOC-CHI', description: 'Chicago Office' },
          { code: 'LOC-BOS', description: 'Boston Office' },
          { code: 'LOC-SEA', description: 'Seattle Office' },
          { code: 'LOC-AUS', description: 'Austin Office' },
          { code: 'LOC-MIA', description: 'Miami Office' },
          { code: 'LOC-LON', description: 'London Office' },
          { code: 'LOC-PAR', description: 'Paris Office' },
          { code: 'LOC-BER', description: 'Berlin Office' },
          { code: 'LOC-TOK', description: 'Tokyo Office' },
          { code: 'LOC-SYD', description: 'Sydney Office' },
          { code: 'LOC-SIN', description: 'Singapore Office' },
          { code: 'LOC-HK', description: 'Hong Kong Office' },
          { code: 'LOC-DUB', description: 'Dubai Office' },
          { code: 'LOC-TOR', description: 'Toronto Office' },
          { code: 'LOC-MEX', description: 'Mexico City Office' },
          { code: 'LOC-SAO', description: 'São Paulo Office' },
          { code: 'LOC-MUM', description: 'Mumbai Office' },
          { code: 'LOC-BAN', description: 'Bangalore Office' },
          { code: 'LOC-SHA', description: 'Shanghai Office' },
          { code: 'LOC-WFH', description: 'Work From Home' },
          { code: 'LOC-REM', description: 'Remote' },
        ],
      },
      Project: {
        values: [
          { code: 'P001', description: 'ERP Implementation' },
          { code: 'P002', description: 'Digital Transformation' },
          { code: 'P003', description: 'Product Launch Q1' },
          { code: 'P004', description: 'Cost Optimization' },
          { code: 'P005', description: 'Market Expansion' },
          { code: 'P006', description: 'Infrastructure Upgrade' },
          { code: 'P007', description: 'Customer Experience Enhancement' },
          { code: 'P008', description: 'Data Migration' },
          { code: 'P009', description: 'Compliance Program' },
          { code: 'P010', description: 'Process Automation' },
          { code: 'P011', description: 'Cloud Migration' },
          { code: 'P012', description: 'Security Enhancement' },
          { code: 'P013', description: 'Mobile App Development' },
          { code: 'P014', description: 'Supply Chain Optimization' },
          { code: 'P015', description: 'Talent Development Program' },
          { code: 'P016', description: 'Brand Refresh' },
          { code: 'P017', description: 'Sustainability Initiative' },
          { code: 'P018', description: 'M&A Integration' },
          { code: 'P019', description: 'Product Redesign' },
          { code: 'P020', description: 'Sales Enablement' },
          { code: 'P021', description: 'Office Expansion' },
          { code: 'P022', description: 'System Consolidation' },
          { code: 'P023', description: 'Customer Portal' },
          { code: 'P024', description: 'Analytics Platform' },
          { code: 'BAU', description: 'Business As Usual' },
        ],
        validCombinations:
          dimensionCount >= 9
            ? {
                'LOC-HQ': ['P001', 'P002', 'P004', 'P009', 'P018', 'BAU'],
                'LOC-SF': ['P002', 'P013', 'P023', 'P024', 'BAU'],
                'LOC-LA': ['P003', 'P016', 'P020', 'BAU'],
                'LOC-CHI': ['P004', 'P014', 'P022', 'BAU'],
                'LOC-BOS': ['P001', 'P009', 'P012', 'BAU'],
                'LOC-SEA': ['P002', 'P011', 'P013', 'BAU'],
                'LOC-AUS': ['P010', 'P013', 'P020', 'BAU'],
                'LOC-MIA': ['P005', 'P020', 'BAU'],
                'LOC-LON': ['P001', 'P005', 'P009', 'P018', 'BAU'],
                'LOC-PAR': ['P005', 'P016', 'BAU'],
                'LOC-BER': ['P002', 'P012', 'BAU'],
                'LOC-TOK': ['P003', 'P005', 'P014', 'BAU'],
                'LOC-SYD': ['P005', 'P007', 'BAU'],
                'LOC-SIN': ['P005', 'P014', 'P024', 'BAU'],
                'LOC-HK': ['P005', 'P018', 'BAU'],
                'LOC-DUB': ['P005', 'P021', 'BAU'],
                'LOC-TOR': ['P005', 'P015', 'BAU'],
                'LOC-MEX': ['P005', 'P014', 'BAU'],
                'LOC-SAO': ['P005', 'P014', 'BAU'],
                'LOC-MUM': ['P002', 'P010', 'P024', 'BAU'],
                'LOC-BAN': ['P002', 'P013', 'P024', 'BAU'],
                'LOC-SHA': ['P003', 'P005', 'P014', 'BAU'],
                'LOC-WFH': ['P002', 'P010', 'P013', 'P020', 'BAU'],
                'LOC-REM': ['P002', 'P010', 'P013', 'P020', 'BAU'],
              }
            : {
                '1010': ['P001', 'P004', 'P009', 'P018', 'BAU'],
                '1020': ['P004', 'P018', 'BAU'],
                '1030': ['P004', 'P018', 'BAU'],
                '1040': ['P010', 'P015', 'BAU'],
                '1050': ['P009', 'BAU'],
                '1060': ['P009', 'BAU'],
                '1070': ['P009', 'P012', 'BAU'],
                '1080': ['P009', 'BAU'],
                '2010': ['P003', 'P004', 'P010', 'P014', 'P019', 'BAU'],
                '2020': ['P004', 'P005', 'P014', 'BAU'],
                '2030': ['P014', 'P021', 'BAU'],
                '2040': ['P003', 'P019', 'BAU'],
                '2050': ['P010', 'P014', 'BAU'],
                '2060': ['P004', 'P014', 'BAU'],
                '2070': ['P014', 'BAU'],
                '3010': ['P003', 'P005', 'P007', 'P020', 'BAU'],
                '3020': ['P005', 'P020', 'BAU'],
                '3030': ['P020', 'BAU'],
                '3040': ['P003', 'P020', 'BAU'],
                '3050': ['P007', 'P023', 'BAU'],
                '3060': ['P007', 'BAU'],
                '4010': ['P001', 'P002', 'P006', 'P011', 'P022', 'BAU'],
                '4020': ['P001', 'P002', 'P013', 'P023', 'BAU'],
                '4030': ['P012', 'BAU'],
                '4040': ['P008', 'P024', 'BAU'],
                '4050': ['P024', 'BAU'],
                '4060': ['P006', 'BAU'],
                '5010': ['P015', 'BAU'],
                '5020': ['P010', 'P015', 'BAU'],
                '5030': ['P015', 'BAU'],
                '5040': ['P015', 'BAU'],
              },
      },
      Activity: {
        values: [
          { code: 'A100', description: 'Salaries' },
          { code: 'A110', description: 'Bonuses & Incentives' },
          { code: 'A120', description: 'Benefits' },
          { code: 'A130', description: 'Contractor Fees' },
          { code: 'A200', description: 'Consulting' },
          { code: 'A210', description: 'Professional Services' },
          { code: 'A220', description: 'Legal Fees' },
          { code: 'A230', description: 'Audit Fees' },
          { code: 'A300', description: 'Software' },
          { code: 'A310', description: 'Software Licenses' },
          { code: 'A320', description: 'SaaS Subscriptions' },
          { code: 'A330', description: 'Software Development' },
          { code: 'A400', description: 'Hardware' },
          { code: 'A410', description: 'Computer Equipment' },
          { code: 'A420', description: 'Network Equipment' },
          { code: 'A430', description: 'Server Equipment' },
          { code: 'A500', description: 'Travel' },
          { code: 'A510', description: 'Accommodation' },
          { code: 'A520', description: 'Meals & Entertainment' },
          { code: 'A600', description: 'Marketing' },
          { code: 'A610', description: 'Advertising' },
          { code: 'A620', description: 'Events & Conferences' },
          { code: 'A630', description: 'Marketing Materials' },
          { code: 'A700', description: 'Training' },
          { code: 'A710', description: 'Training Materials' },
          { code: 'A720', description: 'Certifications' },
          { code: 'A800', description: 'Office Supplies' },
          { code: 'A810', description: 'Facilities' },
          { code: 'A820', description: 'Utilities' },
          { code: 'A900', description: 'Telecommunications' },
        ],
        validCombinations: {
          P001: [
            'A100',
            'A130',
            'A200',
            'A300',
            'A310',
            'A320',
            'A330',
            'A700',
          ],
          P002: ['A100', 'A130', 'A200', 'A300', 'A320', 'A330', 'A700'],
          P003: ['A100', 'A110', 'A500', 'A600', 'A610', 'A620', 'A630'],
          P004: ['A100', 'A200', 'A210'],
          P005: ['A100', 'A110', 'A500', 'A510', 'A600', 'A610', 'A620'],
          P006: ['A300', 'A310', 'A400', 'A410', 'A420', 'A430', 'A200'],
          P007: ['A100', 'A200', 'A300', 'A320', 'A700'],
          P008: ['A100', 'A130', 'A200', 'A300', 'A330'],
          P009: ['A100', 'A200', 'A220', 'A230', 'A700'],
          P010: ['A100', 'A200', 'A300', 'A320', 'A330'],
          P011: ['A100', 'A200', 'A300', 'A320', 'A400'],
          P012: ['A100', 'A200', 'A300', 'A310', 'A400'],
          P013: ['A100', 'A130', 'A200', 'A300', 'A330'],
          P014: ['A100', 'A200', 'A300', 'A320'],
          P015: ['A100', 'A200', 'A700', 'A710', 'A720'],
          P016: ['A100', 'A200', 'A600', 'A610', 'A630'],
          P017: ['A100', 'A200', 'A600'],
          P018: ['A100', 'A200', 'A220', 'A230'],
          P019: ['A100', 'A130', 'A200', 'A300'],
          P020: ['A100', 'A200', 'A300', 'A320', 'A700'],
          P021: ['A100', 'A400', 'A800', 'A810'],
          P022: ['A100', 'A200', 'A300', 'A320'],
          P023: ['A100', 'A130', 'A200', 'A300', 'A330'],
          P024: ['A100', 'A200', 'A300', 'A320', 'A330'],
          BAU: [
            'A100',
            'A110',
            'A120',
            'A130',
            'A200',
            'A210',
            'A220',
            'A230',
            'A300',
            'A310',
            'A320',
            'A400',
            'A410',
            'A500',
            'A510',
            'A520',
            'A600',
            'A610',
            'A620',
            'A630',
            'A700',
            'A710',
            'A800',
            'A810',
            'A820',
            'A900',
          ],
        },
      },
      'Product Line': {
        values: [
          { code: 'PL-ENT', description: 'Enterprise Solutions' },
          { code: 'PL-SMB', description: 'Small & Medium Business' },
          { code: 'PL-CON', description: 'Consumer Products' },
          { code: 'PL-PRO', description: 'Professional Services' },
          { code: 'PL-EDU', description: 'Education' },
          { code: 'PL-GOV', description: 'Government' },
          { code: 'PL-HCR', description: 'Healthcare' },
          { code: 'PL-FIN', description: 'Financial Services' },
          { code: 'PL-RET', description: 'Retail' },
          { code: 'PL-MFG', description: 'Manufacturing' },
          { code: 'PL-TEC', description: 'Technology' },
          { code: 'PL-TEL', description: 'Telecommunications' },
          { code: 'PL-ENR', description: 'Energy' },
          { code: 'PL-TRN', description: 'Transportation' },
          { code: 'PL-MED', description: 'Media' },
          { code: 'PL-HSP', description: 'Hospitality' },
          { code: 'PL-AGR', description: 'Agriculture' },
          { code: 'PL-CNS', description: 'Construction' },
          { code: 'PL-PHR', description: 'Pharmaceutical' },
          { code: 'PL-AUT', description: 'Automotive' },
        ],
      },
      Customer: {
        values: [
          { code: 'CUST-001', description: 'Acme Corporation' },
          { code: 'CUST-002', description: 'Global Industries Inc' },
          { code: 'CUST-003', description: 'Tech Solutions Ltd' },
          { code: 'CUST-004', description: 'Retail Partners Co' },
          { code: 'CUST-005', description: 'Financial Group LLC' },
          { code: 'CUST-006', description: 'Healthcare Systems' },
          { code: 'CUST-007', description: 'Education Alliance' },
          { code: 'CUST-008', description: 'Government Services' },
          { code: 'CUST-009', description: 'Manufacturing United' },
          { code: 'CUST-010', description: 'Energy Partners' },
          { code: 'CUST-011', description: 'Telecom Networks' },
          { code: 'CUST-012', description: 'Media Group' },
          { code: 'CUST-013', description: 'Hospitality Holdings' },
          { code: 'CUST-014', description: 'Transport Solutions' },
          { code: 'CUST-015', description: 'Agricultural Co-op' },
          { code: 'CUST-016', description: 'Construction Partners' },
          { code: 'CUST-017', description: 'Pharma Research' },
          { code: 'CUST-018', description: 'Auto Manufacturers' },
          { code: 'CUST-019', description: 'Real Estate Group' },
          { code: 'CUST-020', description: 'Consulting Firm' },
          { code: 'INTERNAL', description: 'Internal Use' },
          { code: 'GENERAL', description: 'General / Not Applicable' },
        ],
      },
      Region: {
        values: [
          { code: 'NA-US', description: 'North America - United States' },
          { code: 'NA-CA', description: 'North America - Canada' },
          { code: 'NA-MX', description: 'North America - Mexico' },
          { code: 'EU-UK', description: 'Europe - United Kingdom' },
          { code: 'EU-DE', description: 'Europe - Germany' },
          { code: 'EU-FR', description: 'Europe - France' },
          { code: 'EU-ES', description: 'Europe - Spain' },
          { code: 'EU-IT', description: 'Europe - Italy' },
          { code: 'EU-NL', description: 'Europe - Netherlands' },
          { code: 'EU-SE', description: 'Europe - Sweden' },
          { code: 'APAC-AU', description: 'Asia Pacific - Australia' },
          { code: 'APAC-NZ', description: 'Asia Pacific - New Zealand' },
          { code: 'APAC-JP', description: 'Asia Pacific - Japan' },
          { code: 'APAC-CN', description: 'Asia Pacific - China' },
          { code: 'APAC-IN', description: 'Asia Pacific - India' },
          { code: 'APAC-SG', description: 'Asia Pacific - Singapore' },
          { code: 'APAC-HK', description: 'Asia Pacific - Hong Kong' },
          { code: 'LATAM-BR', description: 'Latin America - Brazil' },
          { code: 'LATAM-AR', description: 'Latin America - Argentina' },
          { code: 'LATAM-CL', description: 'Latin America - Chile' },
          { code: 'MEA-AE', description: 'Middle East & Africa - UAE' },
          {
            code: 'MEA-SA',
            description: 'Middle East & Africa - Saudi Arabia',
          },
          {
            code: 'MEA-ZA',
            description: 'Middle East & Africa - South Africa',
          },
          { code: 'GLOBAL', description: 'Global' },
        ],
      },
    },
  }
}
